import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Snackbar,
  Skeleton,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Refresh,
  Star,
  StarBorder,
  Image,
  Close,
  Save,
  Category,
  ArrowUpward,
  ArrowDownward,
  SwapVert,
  DragIndicator
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { blue, amber } from '@mui/material/colors';
import pb from './pocketbase';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CategoryImage = styled('img')({
  width: 70,
  height: 70,
  objectFit: 'cover',
  borderRadius: 8,
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
}));

const FeaturedChip = styled(Chip)({
  backgroundColor: amber[100],
  color: amber[800],
  fontWeight: 600,
});

const Categories = () => {
  // Current date and user
  const currentDateTime = "2025-04-03 04:37:12";
  const currentUser = "MdAbuNafisNiloy";

  // State management
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCategories, setTotalCategories] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    featured: false,
    image: null,
    position: 0
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('position'); // Default sort by position
  const [sortDirection, setSortDirection] = useState('asc'); // asc or desc
  const [reorderMode, setReorderMode] = useState(false); // For drag-and-drop mode

  // Fetch categories from PocketBase
  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      // Create filter based on search query
      let filter = '';
      if (searchQuery) {
        filter = `name~"${searchQuery}"`;
      }
      
      // Fetch categories with pagination
      const resultList = await pb.collection('categories').getList(page + 1, rowsPerPage, {
        filter,
        sort: `${sortDirection === 'desc' ? '-' : ''}${sortBy}`
      });
      
      setCategories(resultList.items);
      setTotalCategories(resultList.totalItems);
      
      // Check if positions need to be initialized
      if (resultList.items.length > 0 && resultList.items.some(item => item.position === undefined || item.position === null)) {
        await initializeCategoryPositions(resultList.items);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load categories',
        severity: 'error'
      });
      setLoading(false);
    }
  };
  
  // Initialize positions for categories that don't have them
  const initializeCategoryPositions = async (categoriesList) => {
    try {
      setIsSubmitting(true);
      
      // Get all categories sorted by creation date
      const allCategories = await pb.collection('categories').getFullList({
        sort: 'created'
      });
      
      // Assign positions based on creation order
      const updates = [];
      
      allCategories.forEach((category, index) => {
        if (category.position === undefined || category.position === null) {
          updates.push(pb.collection('categories').update(category.id, {
            position: index + 1 // Starting from 1 for better readability
          }));
        }
      });
      
      if (updates.length > 0) {
        await Promise.all(updates);
        
        setSnackbar({
          open: true,
          message: `Initialized positions for ${updates.length} categories`,
          severity: 'success'
        });
        
        // Refetch categories to get updated positions
        fetchCategories();
      }
      
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error initializing category positions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to initialize category positions',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Handle position change for a category
  const handlePositionChange = async (categoryId, newPosition) => {
    try {
      setIsSubmitting(true);
      
      await pb.collection('categories').update(categoryId, {
        position: parseInt(newPosition)
      });
      
      setSnackbar({
        open: true,
        message: 'Category position updated',
        severity: 'success'
      });
      
      fetchCategories();
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error updating category position:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update category position',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };
  
  // Handle drag-and-drop reordering
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const { source, destination } = result;
    
    if (source.index === destination.index) return;
    
    try {
      setIsSubmitting(true);
      
      // Create a copy of categories array
      const reorderedCategories = Array.from(categories);
      
      // Remove the dragged item from the array
      const [reorderedItem] = reorderedCategories.splice(source.index, 1);
      
      // Insert the item at the destination index
      reorderedCategories.splice(destination.index, 0, reorderedItem);
      
      // Update positions for all affected categories
      const updates = reorderedCategories.map((category, index) => {
        return pb.collection('categories').update(category.id, {
          position: index + 1
        });
      });
      
      await Promise.all(updates);
      
      setSnackbar({
        open: true,
        message: 'Categories reordered successfully',
        severity: 'success'
      });
      
      fetchCategories();
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error reordering categories:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reorder categories',
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };
  
  // Toggle reorder mode
  const toggleReorderMode = () => {
    if (reorderMode) {
      fetchCategories(); // Refresh the list after reordering
    } else {
      // When entering reorder mode, make sure we sort by position
      setSortBy('position');
      setSortDirection('asc');
    }
    setReorderMode(!reorderMode);
  };

  // Load categories when component mounts or dependencies change
  useEffect(() => {
    fetchCategories();
  }, [page, rowsPerPage, searchQuery, sortBy, sortDirection]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle dialog open for adding new category
  const handleAddClick = () => {
    setDialogMode('add');
    setFormData({
      name: '',
      featured: false,
      image: null,
      position: categories.length > 0 ? Math.max(...categories.map(c => c.position || 0)) + 1 : 1
    });
    setPreviewImage(null);
    setOpenDialog(true);
  };

  // Handle dialog open for editing category
  const handleEditClick = (category) => {
    setDialogMode('edit');
    setCurrentCategory(category);
    setFormData({
      name: category.name,
      featured: category.featured,
      position: category.position || 0
    });
    
    // Set preview image if exists
    if (category.image) {
      const imageUrl = `${pb.baseUrl}/api/files/${category.collectionId}/${category.id}/${category.image}`;
      setPreviewImage(imageUrl);
    } else {
      setPreviewImage(null);
    }
    
    setOpenDialog(true);
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and reset direction to asc
      setSortBy(field);
      setSortDirection('asc');
    }
    setPage(0);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  // Handle form field changes
  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'position' ? parseInt(value) || 0 : value
    }));
  };

  // Handle image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      
      // Create image preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!formData.name.trim()) {
        setSnackbar({
          open: true,
          message: 'Category name is required',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('featured', formData.featured);
      formDataToSend.append('position', formData.position);
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }
      
      if (dialogMode === 'add') {
        // Create new category
        await pb.collection('categories').create(formDataToSend);
        setSnackbar({
          open: true,
          message: 'Category created successfully',
          severity: 'success'
        });
      } else {
        // Update existing category
        await pb.collection('categories').update(currentCategory.id, formDataToSend);
        setSnackbar({
          open: true,
          message: 'Category updated successfully',
          severity: 'success'
        });
      }
      
      // Refresh categories and close dialog
      fetchCategories();
      setOpenDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving category:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${dialogMode === 'add' ? 'create' : 'update'} category: ${error.message}`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmDeleteDialog(true);
  };

  // Handle category deletion
  const handleDeleteConfirm = async () => {
    try {
      setIsSubmitting(true);
      await pb.collection('categories').delete(deleteId);
      
      setSnackbar({
        open: true,
        message: 'Category deleted successfully',
        severity: 'success'
      });
      
      fetchCategories();
      setConfirmDeleteDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error deleting category:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete category: ${error.message}`,
        severity: 'error'
      });
      setIsSubmitting(false);
    }
  };

  // Handle featured status toggle
  const handleToggleFeatured = async (id, currentStatus) => {
    try {
      await pb.collection('categories').update(id, {
        featured: !currentStatus
      });
      
      setSnackbar({
        open: true,
        message: `Category ${!currentStatus ? 'featured' : 'unfeatured'} successfully`,
        severity: 'success'
      });
      
      fetchCategories();
    } catch (error) {
      console.error('Error updating featured status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update featured status',
        severity: 'error'
      });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Categories
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage product categories for your store
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddClick}
          sx={{ borderRadius: 2 }}
          disabled={reorderMode}
        >
          Add Category
        </Button>
      </Box>

      {/* Search and Filter */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search categories..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
              disabled={reorderMode}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => handleSortChange(e.target.value)}
                  disabled={reorderMode}
                >
                  <MenuItem value="position">Position</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="created">Created Date</MenuItem>
                  <MenuItem value="updated">Last Updated</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant={sortDirection === 'asc' ? 'contained' : 'outlined'}
                onClick={() => setSortDirection('asc')}
                size="small"
                disabled={reorderMode}
              >
                ASC
              </Button>
              <Button
                variant={sortDirection === 'desc' ? 'contained' : 'outlined'}
                onClick={() => setSortDirection('desc')}
                size="small"
                disabled={reorderMode}
              >
                DESC
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant={reorderMode ? "contained" : "outlined"}
                startIcon={<SwapVert />}
                onClick={toggleReorderMode}
                size="small"
                color={reorderMode ? "success" : "primary"}
              >
                {reorderMode ? 'Save Order' : 'Reorder Categories'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchQuery('');
                  setSortBy('position');
                  setSortDirection('asc');
                  fetchCategories();
                }}
                size="small"
                disabled={reorderMode}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Categories Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {reorderMode && <StyledTableCell width="50px"></StyledTableCell>}
                <StyledTableCell>Image</StyledTableCell>
                <StyledTableCell>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: reorderMode ? 'default' : 'pointer'
                    }}
                    onClick={() => !reorderMode && handleSortChange('name')}
                  >
                    Name {!reorderMode && getSortIcon('name')}
                  </Box>
                </StyledTableCell>
                <StyledTableCell>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: reorderMode ? 'default' : 'pointer'
                    }}
                    onClick={() => !reorderMode && handleSortChange('position')}
                  >
                    Position {!reorderMode && getSortIcon('position')}
                  </Box>
                </StyledTableCell>
                <StyledTableCell>Featured</StyledTableCell>
                <StyledTableCell>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: reorderMode ? 'default' : 'pointer'
                    }}
                    onClick={() => !reorderMode && handleSortChange('updated')}
                  >
                    Last Updated {!reorderMode && getSortIcon('updated')}
                  </Box>
                </StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Loading skeletons
                Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={70} height={70} sx={{ borderRadius: 2 }} />
                    </TableCell>
                    <TableCell><Skeleton width={150} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={150} /></TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Skeleton variant="circular" width={40} height={40} />
                        <Skeleton variant="circular" width={40} height={40} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Category sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        No Categories Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchQuery ? 'Try adjusting your search.' : 'Create your first category to get started.'}
                      </Typography>
                      {!searchQuery && (
                        <Button 
                          variant="contained" 
                          startIcon={<Add />}
                          onClick={handleAddClick}
                        >
                          Add Category
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : reorderMode ? (
                // Draggable categories in reorder mode
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="categories">
                    {(provided) => (
                      <tbody
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {categories.map((category, index) => (
                          <Draggable
                            key={category.id}
                            draggableId={category.id}
                            index={index}
                          >
                            {(provided) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{ 
                                  '&:hover': { 
                                    bgcolor: 'action.hover',
                                  },
                                  bgcolor: index % 2 ? 'background.default' : 'inherit',
                                }}
                              >
                                <TableCell {...provided.dragHandleProps}>
                                  <DragIndicator color="action" />
                                </TableCell>
                                <TableCell>
                                  {category.image ? (
                                    <CategoryImage
                                      src={`${pb.baseUrl}/api/files/${category.collectionId}/${category.id}/${category.image}`}
                                      alt={category.name}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/70?text=No+Image';
                                      }}
                                    />
                                  ) : (
                                    <Box 
                                      sx={{ 
                                        width: 70, 
                                        height: 70, 
                                        bgcolor: 'grey.200',
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Image color="action" />
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1" fontWeight={500}>
                                    {category.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1">
                                    {category.position || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {category.featured ? (
                                    <FeaturedChip
                                      icon={<Star fontSize="small" />}
                                      label="Featured"
                                      size="small"
                                    />
                                  ) : (
                                    <Chip
                                      label="Not Featured"
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatDate(category.updated)}
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    Drag to reorder
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                // Regular table display
                categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      {category.image ? (
                        <CategoryImage
                          src={`${pb.baseUrl}/api/files/${category.collectionId}/${category.id}/${category.image}`}
                          alt={category.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/70?text=No+Image';
                          }}
                        />
                      ) : (
                        <Box 
                          sx={{ 
                            width: 70, 
                            height: 70, 
                            bgcolor: 'grey.200',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Image color="action" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {category.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={category.position || 0}
                        onChange={(e) => handlePositionChange(category.id, e.target.value)}
                        InputProps={{ 
                          inputProps: { min: 1 },
                          sx: { width: 80 }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {category.featured ? (
                        <FeaturedChip
                          icon={<Star fontSize="small" />}
                          label="Featured"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="Not Featured"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(category.updated)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title={category.featured ? "Remove from featured" : "Mark as featured"}>
                          <IconButton
                            color={category.featured ? "warning" : "default"}
                            onClick={() => handleToggleFeatured(category.id, category.featured)}
                          >
                            {category.featured ? <Star /> : <StarBorder />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Category">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(category)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Category">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(category.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100,500]}
          component="div"
          count={totalCategories}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          disabled={reorderMode}
        />
      </Paper>

      {/* Add/Edit Category Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={!isSubmitting ? handleDialogClose : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {dialogMode === 'add' ? 'Add New Category' : 'Edit Category'}
            </Typography>
            <IconButton
              onClick={handleDialogClose}
              disabled={isSubmitting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                error={formData.name.trim() === ''}
                helperText={formData.name.trim() === '' ? 'Category name is required' : ''}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.featured}
                      onChange={handleFormChange}
                      name="featured"
                    />
                  }
                  label="Featured Category"
                />
              </FormGroup>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                type="number"
                value={formData.position}
                onChange={handleFormChange}
                InputProps={{ inputProps: { min: 1 } }}
                helperText="Lower numbers appear first"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Category Image
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<Image />}
                    disabled={isSubmitting}
                  >
                    {previewImage ? 'Change Image' : 'Upload Image'}
                    <VisuallyHiddenInput 
                      type="file" 
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                  
                  {previewImage && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setPreviewImage(null);
                        setFormData(prev => ({ ...prev, image: null }));
                      }}
                      disabled={isSubmitting}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
              
              {previewImage && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 8
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleDialogClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || formData.name.trim() === ''}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save Category'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialog}
        onClose={!isSubmitting ? () => setConfirmDeleteDialog(false) : undefined}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete this category? This action cannot be undone.
          </Alert>
          <Typography variant="body2">
            Products in this category will not be deleted, but they will no longer be associated with this category.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Delete />}
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Categories;